import { useEffect, useRef, useState } from "react";

const DEFAULT_COOLDOWN_MS = 5000;

// Wraps a report "generate" request so its triggering button can be kept disabled not just while
// the request is in flight, but for a short cooldown after it completes successfully - this stops
// an impatient double-click from firing a second, functionally identical request. Pure UX nicety:
// the actual duplicate-prevention guarantee lives server-side (see UserReport.DedupeKey in the
// API), this only avoids unnecessary round-trips for the common case. On failure the cooldown is
// skipped so the user can immediately retry.
export default function useReportRequestCooldown(cooldownMs: number = DEFAULT_COOLDOWN_MS) {
	const [disabled, setDisabled] = useState(false);
	const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

	useEffect(() => () => clearTimeout(timeoutRef.current), []);

	const run = async (request: () => Promise<void>) => {
		if (disabled) {
			return;
		}

		setDisabled(true);

		try {
			await request();
			timeoutRef.current = setTimeout(() => setDisabled(false), cooldownMs);
		} catch (e) {
			setDisabled(false);
			throw e;
		}
	};

	return [run, disabled] as const;
}
